import { useEffect, useState } from 'react'
import { FileSpreadsheet } from 'lucide-react'
import Button from '../../components/Button'
import ExcelUploadFields from '../../components/ExcelUploadFields'
import { FormBody } from '../../components/Form'
import FormRow from '../../components/FormRow'
import Select from '../../components/Select'
import { saveWorkbook } from '../../lib/spreadsheet'
import Modal from '../../components/Modal'
import { strings as S } from './strings'
import './modals.css'

const ACCEPT = '.csv,.xls,.xlsx'
const EXTENSIONS = ['csv', 'xls', 'xlsx']

/**
 * `#add_modal` in excel mode — `showCreationForm(true, type)`.
 *
 * All four live file types share one body; only the title, the hint, the column
 * table and the template link change (js.cshtml:489-538). The fifth type in the
 * legacy switch (`Users`) is unreachable because its menu item is commented out.
 */
export default function UserImportModal({ open, type, onClose, onSubmit, sources = [], defaultSourceId }) {
  const [file, setFile] = useState(null)
  const [error, setError] = useState('')
  const [sourceId, setSourceId] = useState('')
  const [saving, setSaving] = useState(false)
  const ldap = type === 'ActiveDirectoryUsers'
  const ldapSources = sources.filter(source => source.type?.toUpperCase() === 'LDAP')

  useEffect(() => {
    if (!open) return
    setFile(null)
    setError('')
    setSaving(false)
    const choices = sources.filter(source => source.type?.toUpperCase() === 'LDAP')
    setSourceId(choices.some(source => String(source.id) === String(defaultSourceId)) ? String(defaultSourceId) : choices.length === 1 ? String(choices[0].id) : '')
  }, [open, type, sources, defaultSourceId])

  const config = S.importTypes[type]
  if (!config) return null

  const submit = async (event) => {
    event.preventDefault()
    if (saving) return
    if (ldap && !sourceId) { setError(S.clone.sourcePlaceholder); return }
    const extension = file?.name.split('.').pop()?.toLowerCase()
    if (!file || !EXTENSIONS.includes(extension)) {
      setError(S.importModal.fileError)
      return
    }
    setSaving(true)
    try { await onSubmit({ fileType: type, file, sourceId: ldap ? sourceId : undefined }) }
    finally { setSaving(false) }
  }

  return (
    <Modal
      open={open}
      title={config.title}
      icon={FileSpreadsheet}
      hint={config.hint}
      onClose={saving ? undefined : onClose}
      footer={
        <>
          <Button type="submit" form="import-users-form" disabled={saving || (ldap && !sourceId)}>
            {saving ? S.clone.running : S.importModal.save}
          </Button>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            {S.close}
          </Button>
        </>
      }
    >
      <form id="import-users-form" onSubmit={submit} noValidate>
        <FormBody>
          {ldap && <FormRow label={S.clone.source}>{id => <Select id={id} value={sourceId} onChange={setSourceId} options={ldapSources.map(source => ({ value: String(source.id), label: source.name }))} placeholder={S.clone.sourcePlaceholder} />}</FormRow>}
          <ExcelUploadFields
            id="import-users-file"
            name="csv"
            accept={ACCEPT}
            columns={config.columns}
            templateHref={`/root/ExcelTemplates/Users_${type}.xlsx`}
            onDownloadTemplate={ldap ? () => saveWorkbook([['Username']], { fileName: 'LDAP_Usernames.xlsx', sheetName: 'Users', columnWidths: [36] }).catch(() => setError(S.importModal.fileError)) : undefined}
            error={error}
            strings={S.importModal}
            onChange={(selected) => {
              setFile(selected)
              setError('')
            }}
          />
        </FormBody>
      </form>
    </Modal>
  )
}
